import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { translations } from './translations';
import './App.css';

function Contact() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Rensa fel när användaren börjar skriva igen
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Automatiskt detektera API URL: använd relativ sökväg på Vercel, annars localhost
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
      const API_URL = process.env.REACT_APP_API_URL || (isProduction ? '' : 'http://localhost:5000');
      
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: language,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Serverfel: ${response.status}`);
      }

      // Framgång!
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      // Återställ efter 5 sekunder
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      let errorMessage = language === 'sv' 
        ? 'Ett fel uppstod. Försök igen senare.'
        : 'An error occurred. Please try again later.';
      
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        errorMessage = language === 'sv'
          ? 'Kunde inte ansluta till servern. Kontrollera att servern körs på port 5000.'
          : 'Could not connect to server. Please ensure the server is running on port 5000.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('Fel vid skickande av formulär:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <header className="topbar">
        <Link to="/" className="brand">
          <img src="/velyntor_logo-removebg-preview.png" alt="Velyntor" className="brand-logo" />
        </Link>
        <div className="brand-center">VELYNTOR</div>
        <button
          className={`menu-toggle ${menuOpen ? 'open' : ''}`}
          aria-label="Öppna meny"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="topbar-right">
          <nav className={`nav ${menuOpen ? 'open' : ''}`}>
            <Link to="/" onClick={() => setMenuOpen(false)}>
              {t.automation}
            </Link>
            <Link to="/marknadsforing" onClick={() => setMenuOpen(false)}>
              {t.marketing}
            </Link>
            <Link to="/webbutveckling" onClick={() => setMenuOpen(false)}>
              {t.webDevelopment}
            </Link>
            <Link to="/om-oss" onClick={() => setMenuOpen(false)}>
              {t.about}
            </Link>
            <Link to="/kontakt" onClick={() => setMenuOpen(false)}>
              {t.contact}
            </Link>
            <Link to="/kontakt" className="ghost-btn mobile-only" onClick={() => setMenuOpen(false)}>
              {t.bookMeeting}
            </Link>
            <button className="language-toggle mobile-only" onClick={toggleLanguage}>
              <img 
                src="https://flagcdn.com/w20/se.png" 
                alt="Svenska" 
                className={`flag-icon ${language === 'sv' ? 'active' : ''}`}
              />
              <span>/</span>
              <img 
                src="https://flagcdn.com/w20/gb.png" 
                alt="English" 
                className={`flag-icon ${language === 'en' ? 'active' : ''}`}
              />
            </button>
          </nav>
          <button className="language-toggle desktop-only" onClick={toggleLanguage}>
            <img 
              src="https://flagcdn.com/w20/se.png" 
              alt="Svenska" 
              className={`flag-icon ${language === 'sv' ? 'active' : ''}`}
            />
            <span>/</span>
            <img 
              src="https://flagcdn.com/w20/gb.png" 
              alt="English" 
              className={`flag-icon ${language === 'en' ? 'active' : ''}`}
            />
          </button>
        </div>
      </header>

      <main>
        <section className="hero contact-hero">
          <div className="hero-text">
            <p className="eyebrow">{t.contactTitle}</p>
            <h1>{t.contactSubtitle}</h1>
            <p className="lede">
              {t.contactDescription}
            </p>
          </div>
        </section>

        <section className="section contact-section">
          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">{t.formName} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t.formNamePlaceholder}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t.formEmail} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t.formEmailPlaceholder}
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">{t.formSubject}</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">{t.formSelectSubject}</option>
                  <option value="Automation">{t.subjectAutomation}</option>
                  <option value="Marknadsföring">{t.subjectMarketing}</option>
                  <option value="Webbutveckling">{t.subjectWebDevelopment}</option>
                  <option value="Annat">{t.subjectOther}</option>
                  
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">{t.formMessage} *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder={t.formMessagePlaceholder}
                />
              </div>

              {error && (
                <div className="form-error" style={{
                  background: '#fee',
                  color: '#c33',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  border: '1px solid #fcc'
                }}>
                  {error}
                </div>
              )}

              {submitted && (
                <div className="form-success">
                  {t.formSuccess}
                </div>
              )}

              <button 
                type="submit" 
                className="primary-btn form-submit"
                disabled={loading}
              >
                {loading ? (language === 'sv' ? 'Skickar...' : 'Sending...') : t.formSendMessage}
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>{t.footerContact}</h4>
            <p style={{ marginTop: '8px' }}>
              <a href="mailto:info@velyntor.se">{t.email}</a>
            </p>
            <p style={{ marginTop: '4px' }}>
              <a href="tel:+46700252952">+46 70 025 29 52</a>
            </p>
            <p style={{ marginTop: '4px' }}>
              <a href="https://linkedin.com/company/techblaze-ab/" target="_blank" rel="noreferrer">
                LinkedIn
              </a>
            </p>
          </div>
          <div className="footer-section">
            <h4>{t.footerPages}</h4>
            <div className="footer-links">
              <Link to="/">{t.automation}</Link>
              <Link to="/marknadsforing">{t.marketing}</Link>
              <Link to="/webbutveckling">{t.webDevelopment}</Link>
              <Link to="/om-oss">{t.about}</Link>
              <Link to="/kontakt">{t.contact}</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div>© {new Date().getFullYear()} Velyntor. {t.footerRights}</div>
        </div>
      </footer>
    </div>
  );
}

export default Contact;

